import { useEffect, useState } from "react";
import { CheckCircle2, Circle, FileText, X } from "lucide-react";
import api from "../../lib/api";
import { Badge } from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import { formatPrice } from "../../lib/utils";

const ACTION_LABEL = {
  approve: "Approve — make it live",
  request_info: "Request more information",
  reject: "Reject",
  suspend: "Suspend",
  reinstate: "Reinstate — make it live again",
  reopen: "Reopen — let the owner submit again",
  clear_flags: "Clear flags — keep it live",
};

const CHECK_LABEL = {
  phone: "Phone identity",
  identity: "Provider identity",
  business_registration: "Business registration",
  professional_license: "Professional licence",
  payout: "Payout eligibility",
};

const CHECK_VARIANT = { verified: "accent", pending: "warning", rejected: "dark", failed: "dark" };

const LOG_LABEL = {
  created: "Shop created",
  auto_approved: "Published automatically",
  submitted_for_review: "Sent to manual review",
  flagged: "Flagged automatically",
  approve: "Approved",
  request_info: "More information requested",
  reject: "Rejected",
  suspend: "Suspended",
  reinstate: "Reinstated",
  reopen: "Reopened",
  clear_flags: "Flags cleared",
  verification_submitted: "Check submitted",
  verification_verified: "Check passed",
  verification_rejected: "Check failed",
};

const words = (value) => String(value || "").replace(/_/g, " ");
const errorOf = (err) => Object.values(err?.response?.data?.errors || {})[0]?.[0] || err?.response?.data?.message || "Something went wrong.";

/**
 * Everything an admin needs to decide on one provider: why it was flagged,
 * what it has completed, each verification, and the audit log. Every
 * decision needs a reason.
 */
export default function ProviderReview({ businessId, onClose, onChanged }) {
  const [business, setBusiness] = useState(null);
  const [action, setAction] = useState("");
  const [reason, setReason] = useState("");
  const [cancelUpcoming, setCancelUpcoming] = useState(false);
  const [checkReasons, setCheckReasons] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    api
      .get(`/admin/businesses/${businessId}`)
      .then(({ data }) => setBusiness(data))
      .catch((err) => setError(errorOf(err)));
  }, [businessId]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const run = async (request) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const { data } = await request();
      onChanged();
      return data;
    } catch (err) {
      setError(errorOf(err));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const decide = async (e) => {
    e.preventDefault();
    const data = await run(() =>
      api.post(`/admin/businesses/${businessId}/decision`, { action, reason, cancel_upcoming: cancelUpcoming })
    );
    if (!data) return;
    setBusiness(data.business);
    setAction("");
    setReason("");
    setCancelUpcoming(false);
    if (data.affected_appointments > 0) {
      setNotice(
        data.cancelled_appointments > 0
          ? `${data.cancelled_appointments} upcoming appointment(s) were cancelled and refunds opened. The customers have been told.`
          : `${data.affected_appointments} customer(s) with upcoming appointments were told, and can cancel free of charge.`
      );
    }
  };

  const verify = async (type, status) => {
    const data = await run(() =>
      api.post(`/admin/businesses/${businessId}/verifications/${type}`, { status, reason: checkReasons[type] || "" })
    );
    if (!data) return;
    setBusiness(data);
    setCheckReasons({ ...checkReasons, [type]: "" });
  };

  const openDocument = async (type) => {
    try {
      const { data } = await api.get(`/admin/businesses/${businessId}/verifications/${type}/document`, { responseType: "blob" });
      window.open(URL.createObjectURL(data), "_blank", "noopener");
    } catch (err) {
      setError("Could not open the document.");
    }
  };

  const blocking = ["suspend", "reject"].includes(action);

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/40" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Provider review"
        className="h-full w-full max-w-2xl overflow-y-auto bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-montserrat text-xl font-bold text-slate-900">{business?.name || "Loading…"}</h2>
            {business && (
              <p className="text-xs text-muted-foreground">
                {business.owner?.full_name} · {business.owner?.phone || "no phone"} · {business.owner?.email || "no email"} ·{" "}
                {business.business_type === "freelance" ? "Freelancer" : "Business"} · {business.category?.name || "no category"}
                {business.tin ? ` · TIN ${business.tin}` : ""}
              </p>
            )}
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-full p-1 hover:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </div>

        {error && <p className="mt-3 rounded-xl bg-red-50 p-3 text-sm text-red-700">{error}</p>}
        {notice && <p className="mt-3 rounded-xl bg-emerald-50 p-3 text-sm text-emerald-800">{notice}</p>}

        {business && (
          <div className="mt-4 space-y-6 text-sm">
            <section>
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={business.status === "approved" ? "accent" : "default"}>{words(business.status)}</Badge>
                {business.is_verified && <Badge variant="primary">Verified badge shown</Badge>}
                <Badge variant={business.withdrawals_enabled ? "accent" : "default"}>
                  {business.withdrawals_enabled ? "Withdrawals on" : "Withdrawals off"}
                </Badge>
                {business.upcoming_appointments > 0 && <Badge variant="warning">{business.upcoming_appointments} upcoming appointment(s)</Badge>}
              </div>
              {business.review_note && <p className="mt-2 text-slate-700">Last note to owner: {business.review_note}</p>}
              {business.description && <p className="mt-2 text-muted-foreground">{business.description}</p>}
            </section>

            <section>
              <h3 className="mb-1 font-semibold text-slate-900">Risk flags</h3>
              {business.risk_flags?.length ? (
                <ul className="space-y-1">
                  {business.risk_flags.map((flag) => (
                    <li key={flag.key} className="rounded-lg bg-amber-50 px-3 py-2 text-amber-900">
                      {flag.label}
                      {flag.detail ? <span className="text-amber-700"> — {flag.detail}</span> : null}
                      {business.cleared_risk_flags?.includes(flag.key) && <span className="ml-2 text-xs text-emerald-700">(already accepted)</span>}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted-foreground">None — low risk.</p>
              )}
            </section>

            <section>
              <h3 className="mb-1 font-semibold text-slate-900">Minimum requirements</h3>
              <ul className="space-y-1">
                {business.checklist.map((item) => (
                  <li key={item.key} className="flex items-start gap-2">
                    {item.done ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> : <Circle className="mt-0.5 h-4 w-4 shrink-0 text-slate-300" />}
                    <span>
                      {item.label}
                      {!item.done && item.hint ? <span className="text-muted-foreground"> — {item.hint}</span> : null}
                    </span>
                  </li>
                ))}
              </ul>
            </section>

            <section>
              <h3 className="mb-1 font-semibold text-slate-900">Services</h3>
              {business.services_preview.length ? (
                <ul className="space-y-1">
                  {business.services_preview.map((s) => (
                    <li key={s.id}>
                      <span className="font-medium text-slate-800">{s.name}</span> · {formatPrice(s.price)}
                      {s.description ? <span className="text-muted-foreground"> — {s.description}</span> : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted-foreground">No services yet.</p>
              )}
            </section>

            <section>
              <h3 className="mb-1 font-semibold text-slate-900">Verification</h3>
              <ul className="space-y-3">
                {Object.values(business.verification_checks).map((check) => (
                  <li key={check.type} className="rounded-xl border border-border p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-slate-800">{CHECK_LABEL[check.type]}</span>
                      <Badge variant={CHECK_VARIANT[check.status] || "default"}>{words(check.status)}</Badge>
                      {check.required && check.type !== "phone" && <span className="text-xs text-muted-foreground">required for this category</span>}
                    </div>
                    {check.reference_number && <p className="mt-1 text-xs text-muted-foreground">Number: {check.reference_number}</p>}
                    {check.details?.phone && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {words(check.details.provider).toUpperCase()} {check.details.phone} · {check.details.account_name}
                      </p>
                    )}
                    {check.details?.note && <p className="mt-1 text-xs text-muted-foreground">{check.details.note}</p>}
                    {check.rejection_reason && <p className="mt-1 text-xs text-amber-700">{check.rejection_reason}</p>}
                    {check.has_document && (
                      <button type="button" onClick={() => openDocument(check.type)} className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-primary underline">
                        <FileText className="h-3.5 w-3.5" /> View document
                      </button>
                    )}
                    {check.status !== "not_required" && (
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <input
                          value={checkReasons[check.type] || ""}
                          onChange={(e) => setCheckReasons({ ...checkReasons, [check.type]: e.target.value })}
                          placeholder="Reason / how it was checked"
                          aria-label={`Reason for ${CHECK_LABEL[check.type]}`}
                          className="h-9 min-w-0 flex-1 rounded-lg border border-input px-3 text-xs"
                        />
                        <Button size="sm" disabled={busy || (checkReasons[check.type] || "").trim().length < 5 || check.status === "verified"} onClick={() => verify(check.type, "verified")}>
                          Pass
                        </Button>
                        <Button size="sm" variant="danger" disabled={busy || (checkReasons[check.type] || "").trim().length < 5} onClick={() => verify(check.type, "rejected")}>
                          Fail
                        </Button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </section>

            {business.available_actions.length > 0 && (
              <section>
                <h3 className="mb-1 font-semibold text-slate-900">Decision</h3>
                <form onSubmit={decide} className="space-y-2 rounded-xl border border-border p-3">
                  <select
                    value={action}
                    onChange={(e) => setAction(e.target.value)}
                    aria-label="Decision"
                    required
                    className="h-10 w-full rounded-lg border border-input bg-white px-3 text-sm"
                  >
                    <option value="">Choose a decision…</option>
                    {business.available_actions.map((a) => (
                      <option key={a} value={a}>{ACTION_LABEL[a] || a}</option>
                    ))}
                  </select>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    required
                    minLength={5}
                    rows={3}
                    placeholder={action === "request_info" ? "What should the owner send or change? They will see this." : "Reason (kept in the audit log; the owner sees it when they are turned down)"}
                    aria-label="Reason"
                    className="w-full rounded-lg border border-input px-3 py-2 text-sm"
                  />
                  {blocking && business.upcoming_appointments > 0 && (
                    <label className="flex items-start gap-2 text-xs text-slate-700">
                      <input type="checkbox" checked={cancelUpcoming} onChange={(e) => setCancelUpcoming(e.target.checked)} className="mt-0.5" />
                      <span>
                        Also cancel the {business.upcoming_appointments} upcoming appointment(s) and open full refunds. Left unticked, the
                        appointments stay on record and each customer is told they can cancel free of charge.
                      </span>
                    </label>
                  )}
                  <Button type="submit" size="sm" variant={blocking ? "danger" : "primary"} disabled={busy || !action || reason.trim().length < 5}>
                    {busy ? "Saving…" : "Record decision"}
                  </Button>
                </form>
              </section>
            )}

            <section>
              <h3 className="mb-1 font-semibold text-slate-900">Audit log</h3>
              <ul className="space-y-2">
                {business.audit_log.map((entry) => (
                  <li key={entry.id} className="border-l-2 border-border pl-3">
                    <p className="text-slate-800">
                      <span className="font-medium">{LOG_LABEL[entry.action] || words(entry.action)}</span>
                      {entry.meta?.type ? ` (${words(entry.meta.type)})` : ""}
                      {entry.from_status && entry.to_status && entry.from_status !== entry.to_status ? ` · ${words(entry.from_status)} → ${words(entry.to_status)}` : ""}
                    </p>
                    {entry.reason && <p className="text-slate-600">{entry.reason}</p>}
                    <p className="text-xs text-muted-foreground">
                      {entry.actor?.full_name || "SHUWA (automatic)"} · {new Date(entry.created_at).toLocaleString()}
                      {entry.meta?.flags?.length ? ` · flags: ${entry.meta.flags.map(words).join(", ")}` : ""}
                    </p>
                  </li>
                ))}
              </ul>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
